import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import classService from '../../services/classService';
import { getProfessionPillClasses } from '../../utils/professionColors';
import {
  formatTrack,
  getTrackBadgeClasses,
  getClassStatus,
  getClassStatusPresentation,
  CLASS_STATUS,
} from '../../utils/classDisplay';
import ConfirmationModal from '../common/ConfirmationModal';
import { getInitials } from '../../utils/stringUtils';

const formatDate = (iso) => {
  if (!iso) return 'Not specified';
  const date = new Date(iso);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const ClassDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();

  // State management
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab] = useState('learners');

  // Learners tab state
  const [learners, setLearners] = useState([]);
  const [classMembers, setClassMembers] = useState([]);
  const [selectedLearners, setSelectedLearners] = useState([]);
  const [showLearnerModal, setShowLearnerModal] = useState(false);
  const [learnerSearchTerm, setLearnerSearchTerm] = useState('');
  
  // Confirmation Modal State
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [learnerToRemove, setLearnerToRemove] = useState(null);

  // Check permissions
  const canEdit = currentUser && ['ADMIN', 'BERUFSBILDNER', 'PRACTICE_INSTRUCTOR', 'PRAXISBILDNER'].some(role => 
    currentUser.roles?.some(userRole => userRole.name === role)
  );

  // Determine back navigation based on where we came from
  const getBackNavigation = () => {
    const from = location.state?.from;
    if (from === 'management-hub') {
      return '/management';
    }
    return '/manage/classes';
  };

  // Load class data
  const loadClassData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await classService.getClassById(id);
      setClassData(response);
    } catch (err) {
      setError('Error loading class details: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Load class members
  const loadClassMembers = useCallback(async () => {
    try {
      const response = await classService.getClassMembers(id);
      setClassMembers(response.content || response || []);
    } catch (err) {
      console.error('Error loading class members:', err);
    }
  }, [id]);

  // Load learners for assignment
  const loadLearners = useCallback(async () => {
    if (!classData?.professionType || !canEdit) return;

    try {
      const params = {
        search: learnerSearchTerm || undefined,
        professionType: classData.professionType
      };
      const response = await classService.getLearners(params);
      setLearners(response.content || response || []);
    } catch (err) {
      console.error('Error loading learners:', err);
    }
  }, [learnerSearchTerm, classData, canEdit]);

  // Effects
  useEffect(() => {
    loadClassData();
  }, [id, loadClassData]);

  useEffect(() => {
    if (activeTab === 'learners') {
      loadClassMembers();
      if (canEdit) {
        loadLearners();
      }
    }
  }, [activeTab, loadClassMembers, loadLearners, canEdit]);

  useEffect(() => {
    if (activeTab === 'learners' && canEdit) {
      loadLearners();
    }
  }, [activeTab, learnerSearchTerm, loadLearners, canEdit]);

  // Learner management
  const handleLearnerSelection = (learnerId) => {
    setSelectedLearners(prev => 
      prev.includes(learnerId) 
        ? prev.filter(id => id !== learnerId)
        : [...prev, learnerId]
    );
  };

  const handleAddLearners = async () => {
    try {
      setLoading(true);
      const response = await classService.addMembersToClass(id, selectedLearners);
      
      if (response.ignored && response.ignored.length > 0) {
        setSuccess(`${response.created.length} apprentices added, ${response.ignored.length} already enrolled`);
      } else {
        setSuccess(`${response.created.length} apprentices successfully added`);
      }
      
      setShowLearnerModal(false);
      setSelectedLearners([]);
      loadClassMembers();
      loadClassData(); // Reload to get updated member count
    } catch (err) {
      setError('Error adding learners: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLearner = (learnerId) => {
    setLearnerToRemove(learnerId);
    setConfirmModalOpen(true);
  };

  const confirmRemoveLearner = async () => {
    if (!learnerToRemove) return;

    try {
      setLoading(true);
      await classService.removeMemberFromClass(id, learnerToRemove);
      setSuccess('Apprentice successfully removed');
      loadClassMembers();
      loadClassData(); // Reload to get updated member count
      setConfirmModalOpen(false);
      setLearnerToRemove(null);
    } catch (err) {
      setError('Error removing learner: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  // const getProfessionBadgeVariant = (profession) => {
  //   return profession === 'INFORMATIK' ? 'primary' : 'success';
  // };

  const formatProfession = (profession) => {
    switch (profession) {
      case 'INFORMATIKER':
      case 'INFORMATIK':
        return 'Informatik';
      case 'MEDIAMATIKER':
      case 'MEDIAMATIK':
        return 'Mediamatik';
      case 'KV':
        return 'KV';
      case 'KUNDENDIALOG':
        return 'Kundendialog';
      case 'DETAILHANDEL':
        return 'Detailhandel';
      default:
        return profession;
    }
  };

  // The dialog names who is being removed, so the confirmation is not a
  // generic "this apprentice".
  const removalTarget = classMembers.find(
    (member) => member.id === learnerToRemove,
  );

  const filteredLearners = learners.filter(learner => {
    // Exclude already enrolled learners
    const isAlreadyEnrolled = classMembers.some(member => member.id === learner.id);
    return !isAlreadyEnrolled;
  });

  if (loading && !classData) {
    return (
      <div className="min-h-screen bg-green-cloud flex items-center justify-center">
        <div className="card-brand p-8 text-center">
          <div className="w-16 h-16 bg-sunrise-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-spinner fa-spin text-sunrise-red text-2xl"></i>
          </div>
          <h3 className="text-xl font-bold text-grey-dusk mb-2">Loading Class Details</h3>
          <p className="text-gray-600">Please wait while we fetch the information...</p>
        </div>
      </div>
    );
  }

  if (error && !classData) {
    return (
      <div className="min-h-screen bg-green-cloud flex items-center justify-center p-4">
        <div className="card-brand p-8 text-center max-w-md w-full">
          <div className="w-16 h-16 bg-sunrise-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-exclamation-triangle text-sunrise-red text-2xl"></i>
          </div>
          <h3 className="text-xl font-bold text-grey-dusk mb-4">Error Loading Class</h3>
          <p className="text-slate-cloud mb-6">{error}</p>
          <button 
            onClick={() => navigate(getBackNavigation())}
            className="btn-brand-primary w-full"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            {location.state?.from === 'management-hub' ? 'Back to Management Hub' : 'Back to Classes'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="card-brand-elevated p-6 mb-8 bg-gradient-to-r from-ice-white to-pale-dawn/30">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div className="flex items-center mb-4 lg:mb-0">
              <button
                onClick={() => navigate(getBackNavigation())}
                className="mr-4 p-2 text-gray-600 hover:text-sunrise-red hover:bg-sunrise-red/10 rounded-lg transition-colors duration-200"
                title={location.state?.from === 'management-hub' ? 'Back to Management Hub' : 'Back to Classes'}
              >
                <i className="fas fa-arrow-left text-xl"></i>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-grey-dusk flex items-center">
                  <i className="fas fa-chalkboard-teacher text-sunrise-red mr-4"></i>
                  {classData?.name}
                </h1>
                <p className="text-slate-cloud mt-1">Class Management & Apprentice Administration</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTrackBadgeClasses(classData?.track)}`}>
                {formatTrack(classData?.track)}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getProfessionPillClasses(classData?.professionType)}`}>
                {formatProfession(classData?.professionType)}
              </span>
              {/* Only flag the exceptions - a running class speaks for itself. */}
              {classData && getClassStatus(classData) !== CLASS_STATUS.ACTIVE && (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${getClassStatusPresentation(getClassStatus(classData)).classes}`}
                >
                  <i
                    className={`fas ${getClassStatusPresentation(getClassStatus(classData)).icon} mr-1.5`}
                  ></i>
                  {getClassStatusPresentation(getClassStatus(classData)).label}
                </span>
              )}
            </div>
          </div>

          {/* Class Info */}
          {classData?.description && (
            <div className="bg-white/50 rounded-lg p-4 mb-6">
              <p className="text-grey-dusk leading-relaxed">{classData.description}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            <div className="bg-white/60 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <i className="fas fa-clock text-sunrise-red mr-2"></i>
                <span className="text-sm font-medium text-gray-600">Duration</span>
              </div>
              <p className="text-grey-dusk font-semibold">
                {classData?.startDate && classData?.endDate
                  ? `${formatDate(classData.startDate)} - ${formatDate(classData.endDate)}`
                  : classData?.startDate 
                    ? formatDate(classData.startDate)
                    : 'Not specified'
                }
              </p>
            </div>
            
            <div className="bg-white/60 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <i className="fas fa-users text-sunrise-red mr-2"></i>
                <span className="text-sm font-medium text-gray-600">Enrolled Apprentices</span>
              </div>
              <p className="text-grey-dusk font-semibold text-2xl">
                {classData?.memberCount || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-sunrise-50 border border-sunrise-200 text-sunrise-700 px-6 py-4 rounded-lg flex items-center justify-between">
            <div className="flex items-center">
              <i className="fas fa-exclamation-triangle mr-3"></i>
              <span>{error}</span>
            </div>
            <button 
              onClick={() => setError(null)}
              className="text-sunrise-red hover:text-sunrise-600"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}
        
        {success && (
          <div className="alert-brand-success flex items-center justify-between">
            <div className="flex items-center">
              <i className="fas fa-check-circle mr-3"></i>
              <span>{success}</span>
            </div>
            <button 
              onClick={() => setSuccess(null)}
              className="text-green-500 hover:text-green-600"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Students Section */}
        <div className="bg-ice-white rounded-xl shadow-lg border border-pale-dawn">
          {/* Section Header */}
          {/* Management Hub style header. The primary action keeps the solid
              red fill so it stays the most obvious thing in the section. */}
          <div className="border-b border-pale-dawn/70 bg-pale-dawn/30 px-4 py-3 sm:px-5 rounded-t-xl">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center min-w-0">
                <div className="w-10 h-10 bg-sunrise-red/10 rounded-xl flex items-center justify-center mr-3 shrink-0">
                  <i className="fas fa-users text-sunrise-red"></i>
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-grey-dusk">Enrolled Apprentices</h2>
                  <p className="text-xs text-slate-cloud">
                    {classMembers.length} in this class
                  </p>
                </div>
              </div>
              {canEdit && (
                <button
                  onClick={() => {
                    setSelectedLearners([]);
                    setShowLearnerModal(true);
                  }}
                  className="shrink-0 bg-sunrise-red hover:bg-sunrise-600 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-sm transition-colors duration-200 inline-flex items-center justify-center gap-2"
                >
                  <i className="fas fa-user-plus"></i>
                  Add apprentices
                </button>
              )}
            </div>
          </div>

          {/* Students Content */}
          <div className="p-6">
            {classMembers.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-users text-slate-cloud text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-grey-dusk mb-2">No Apprentices Enrolled</h3>
                <p className="text-gray-600 mb-6">
                  {canEdit ? 'Use the "Add apprentices" button above to enroll apprentices in this class.' : 'No apprentices are enrolled in this class yet.'}
                </p>
                {canEdit && (
                  <button
                    onClick={() => {
                      setSelectedLearners([]);
                      setShowLearnerModal(true);
                    }}
                    className="btn-brand-primary"
                  >
                    <i className="fas fa-user-plus mr-2"></i>
                    Add Your First Apprentices
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {classMembers.map((learner) => (
                  <div key={learner.id} className="bg-gradient-to-br from-ice-white to-pale-dawn/30 rounded-xl p-6 border border-pale-dawn hover:shadow-lg transition-all duration-300">
                    {/* Student Avatar & Info */}
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-sunrise-red to-sunrise-red/80 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                        {getInitials(learner.firstName, learner.lastName, learner.username)}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-grey-dusk text-lg">
                          {learner.firstName} {learner.lastName}
                        </h3>
                        <p className="text-gray-600 text-sm">@{learner.username}</p>
                      </div>
                    </div>

                    {/* Student Details */}
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-envelope w-5 mr-3 text-sunrise-red"></i>
                        <span className="text-sm">{learner.email}</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <i className="fas fa-briefcase w-5 mr-3 text-sunrise-red"></i>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getProfessionPillClasses(learner.professionType)}`}>
                            {formatProfession(learner.professionType)}
                          </span>
                        </div>
                        
                        
                      </div>
                    </div>

                    {/* Actions */}
                    {canEdit && (
                      <div className="pt-4 border-t border-pale-dawn">
                        <button
                          onClick={() => handleRemoveLearner(learner.id)}
                          className="w-full bg-red-50 hover:bg-red-100 text-red-700 px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center"
                        >
                          <i className="fas fa-user-minus mr-2"></i>
                          Remove from Class
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Students Modal */}
      {showLearnerModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-brand-xl border border-pale-dawn w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-sunrise-red to-sunrise-red/90 text-white px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <i className="fas fa-user-plus text-2xl mr-3"></i>
                  <div>
                    <h2 className="text-xl font-bold">Add Apprentices to Class</h2>
                    <p className="text-white/80 text-sm">Select students to enroll in {classData?.name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowLearnerModal(false)}
                  className="text-white/80 hover:text-white text-2xl"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Filters */}
              <div className="mb-6">
                <div>
                  <label className="block text-sm font-medium text-grey-dusk mb-2">Search Apprentices</label>
                  <div className="relative">
                    <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-cloud"></i>
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      value={learnerSearchTerm}
                      onChange={(e) => setLearnerSearchTerm(e.target.value)}
                      className="input-brand pl-10 w-full"
                    />
                  </div>
                  <p className="text-xs text-slate-cloud mt-2">
                    Showing only students for profession: <span className="font-semibold text-grey-dusk">{formatProfession(classData?.professionType)}</span>
                  </p>
                </div>
              </div>

              {/* Available Students */}
              <div className="max-h-96 overflow-y-auto border border-pale-dawn rounded-lg">
                {filteredLearners.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="fas fa-users text-slate-cloud text-4xl mb-4"></i>
                    <h3 className="text-lg font-medium text-grey-dusk mb-2">No Apprentices Available</h3>
                    <p className="text-slate-cloud">
                      {learnerSearchTerm 
                        ? 'No students match your search criteria.' 
                        : 'All available students are already enrolled in this class.'}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 divide-y divide-pale-dawn">
                    {filteredLearners.map((learner) => (
                        <div className="p-4 hover:bg-pale-dawn/30">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedLearners.includes(learner.id)}
                            onChange={() => handleLearnerSelection(learner.id)}
                            className="mr-4 w-4 h-4 text-sunrise-red border-pale-dawn rounded focus:ring-sunrise-red"
                          />
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <div className="w-10 h-10 bg-gradient-to-br from-sunrise-red to-sunrise-red/80 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                                {getInitials(learner.firstName, learner.lastName, learner.username)}
                              </div>
                              <div>
                                <h4 className="font-semibold text-grey-dusk">
                                  {learner.firstName} {learner.lastName}
                                </h4>
                                <p className="text-sm text-slate-cloud">{learner.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getProfessionPillClasses(learner.professionType)}`}>
                                {formatProfession(learner.professionType)}
                              </span>
                             
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Count */}
              {selectedLearners.length > 0 && (
                <div className="mt-4 p-3 bg-sunrise-red/5 border border-sunrise-red/20 rounded-lg">
                  <p className="text-sm text-sunrise-red font-medium">
                    <i className="fas fa-check-circle mr-2"></i>
                    {selectedLearners.length} student{selectedLearners.length !== 1 ? 's' : ''} selected
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-pale-dawn px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => setShowLearnerModal(false)}
                className="px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLearners}
                disabled={selectedLearners.length === 0 || loading}
                className="btn-brand-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Adding...
                  </>
                ) : (
                  <>
                    <i className="fas fa-user-plus mr-2"></i>
                    Add {selectedLearners.length} Apprentice{selectedLearners.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmationModal
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setLearnerToRemove(null);
        }}
        onConfirm={confirmRemoveLearner}
        title="Remove apprentice from class?"
        message={
          <>
            <p>
              <span className="font-semibold text-grey-dusk">
                {removalTarget
                  ? `${removalTarget.firstName} ${removalTarget.lastName}`
                  : "This apprentice"}
              </span>{" "}
              will no longer be enrolled in{" "}
              <span className="font-semibold text-grey-dusk">
                {classData?.name}
              </span>
              .
            </p>
            <p className="mt-2">
              Their grades and reviews are kept - only the class enrolment is
              removed, and you can enrol them again at any time.
            </p>
          </>
        }
        confirmText="Remove from class"
        isDanger={true}
        isLoading={loading}
      />
    </div>
  );
};

export default ClassDetails;
